import React, { useMemo, useState, useEffect } from "react";
import SearchFlightsNumber from "./SearchFlightNumber";
import SearchByRoute from "./SearchByRoute";
import CreditsButton from "./CreditsButton"; // Import the new CreditsButton component
import { getRecentRequests, getUserLoadRequests } from "apis/myRequestsService";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { MyRequestParams, MyRequestResponse } from "@pages/YourRequest/types";
import FlightListing from "./LoadsFlightListing";

const Loads: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [activeTab, setActiveTab] = useState<"flightNumber" | "route">("route");
  const [flights, setFlights] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 5, // Limit to 5 flights
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);

  const handleTabSwitch = (tab: "flightNumber" | "route") => {
    setActiveTab(tab);
  };

  const handleSearchResultsShow = (show: boolean) => {
    setShowSearchResults(show);
  };

  const fetchRecentFlights = async () => {
    showLoader();
    setLoading(true);
    setError(null);

    try {
      const params: MyRequestParams = {
        page: 1,
        size: 5,
        sortBy: "createdDate",
        sortOrder: "desc",
      };
      // const data = await getUserLoadRequests(params);
      const data = await getRecentRequests(params);
      setFlights(data);
    } catch (err) {
      console.error("Error fetching recent flights:", err);
    } finally {
      setLoading(false);
      setTimeout(() => hideLoader(), 500);
    }
  };

  useEffect(() => {
    fetchRecentFlights();
  }, []);

  const activeContent = useMemo(
    () =>
      activeTab === "flightNumber" ? (
        <section className="mt-0 px-2">
          <SearchFlightsNumber
            onShowSearchResults={handleSearchResultsShow}
            onPostRequestSuccess={fetchRecentFlights}
          />
        </section>
      ) : (
        <section className="mt-0 px-2">
          <SearchByRoute
            onShowSearchResults={handleSearchResultsShow}
            onPostRequestSuccess={fetchRecentFlights}
          />
        </section>
      ),
    [activeTab, fetchRecentFlights]
  );

  return (
    <div>
      <section>
        <div className="sm:flex items-center sm:h-[125.5px] h-[175.5px] px-4 relative bg-cover">
          <div className="px-2 sm:mb-8 sm:mt-0 mb-4 mt-4">
            <p className="px-1 text-[#333333] text-lg font-semibold">
              Loads
            </p>
            <div
              className="absolute bottom-0 flex pt-2 self-baseline min-w-64"
              role="tablist"
            >
              {/* Tab backgrounds */}
              <div className="absolute inset-0 flex w-full">
                <div
                  className={`flex-1 transition-colors duration-500 ${
                    activeTab === "route" ? "bg-white" : "bg-[#E6E6E6]"
                  } rounded-tl-lg`}
                ></div>
                <div
                  className={`flex-1 transition-colors duration-500 ${
                    activeTab === "flightNumber" ? "bg-white" : "bg-[#E6E6E6]"
                  } rounded-tr-lg`}
                ></div>
              </div>

              {/* Route Tab */}
              <div
                role="tab"
                aria-selected={activeTab === "route"}
                aria-controls="route-panel"
                className="relative z-10 rounded-tl-lg md:w-fit p-[13px] flex items-center gap-2 cursor-pointer flex-1 -mt-2 justify-center"
                onClick={() => handleTabSwitch("route")}
              >
                <i
                  className={`st-route text-xl transition-colors duration-300 ease-in-out ${
                    activeTab === "route"
                      ? "text-[#FFBC00]"
                      : "text-[#00000080]"
                  }`}
                ></i>
                <p
                  className={`text-sm font-medium transition-colors duration-300 ease-in-out ${
                    activeTab === "route"
                      ? "text-[#FFBC00]"
                      : "text-[#00000080]"
                  }`}
                >
                  Route
                </p>
              </div>

              {/* Flight Number Tab */}
              <div
                role="tab"
                aria-selected={activeTab === "flightNumber"}
                aria-controls="flight-number-panel"
                className="relative z-10 rounded-tr-lg md:w-fit p-[13px] flex items-center gap-2 cursor-pointer flex-1 -mt-2 justify-center"
                onClick={() => handleTabSwitch("flightNumber")}
              >
                <i
                  className={`st-flight text-xl transition-colors duration-300 ease-in-out ${
                    activeTab === "flightNumber"
                      ? "text-[#FFBC00]"
                      : "text-[#00000080]"
                  }`}
                ></i>
                <p
                  className={`text-sm font-medium transition-colors duration-300 ease-in-out ${
                    activeTab === "flightNumber"
                      ? "text-[#FFBC00]"
                      : "text-[#00000080]"
                  }`}
                >
                  Flight No.
                </p>
              </div>
            </div>
          </div>
          <CreditsButton />
        </div>

        {activeContent}
        {error && <p className="text-red-500 mt-4">{error}</p>}

        <div className="px-6">
          <FlightListing
            flights={flights}
            show={showSearchResults}
            FlightLoading={loading}
          />
        </div>
      </section>
    </div>
  );
};

export default Loads;
